import { ArrowRight, Clock3, Download } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { PublicCardLoop, PublicCardPayload } from "../../shared/publicCard";
import { ApiError, api, formatRelative } from "../api";
import { CopyButton, EvidenceChip, ProviderMark, RhythmChart } from "../components";
import { NoirBar, NoirFooter } from "./NoirChrome";
import "./MarketingPage.css";
import "./CardPage.css";

// Public card page. No session, no workspace: everything shown comes from the sanitized public payload.

type CardState =
  | { status: "loading" }
  | { status: "ready"; card: PublicCardPayload }
  | { status: "missing" }
  | { status: "error"; message: string };

const evidenceLabels: Record<PublicCardLoop["evidence"], string> = {
  cataloged: "Cataloged",
  connected: "Connected",
  reporting: "Reporting",
  evaluable: "Enough evidence",
};

function kindLabel(kind: PublicCardPayload["kind"]): string {
  return kind === "loop" ? "Loop card" : kind === "collection" ? "Collection" : "Status card";
}

function fileSlug(title: string): string {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/gu, "-").replace(/^-+|-+$/gu, "").slice(0, 48);
  return slug || "loopgroup-card";
}

export function CardPage() {
  const { token = "" } = useParams();
  const [state, setState] = useState<CardState>({ status: "loading" });
  const pageUrl = `${window.location.origin}/c/${token}`;

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    void api<{ card: PublicCardPayload }>(`/api/public/cards/${encodeURIComponent(token)}`, { signal: controller.signal })
      .then((data) => setState({ status: "ready", card: data.card }))
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        if (caught instanceof ApiError && caught.status === 404) setState({ status: "missing" });
        else setState({ status: "error", message: caught instanceof Error ? caught.message : "This card could not be loaded." });
      });
    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    document.title = state.status === "ready" ? `${state.card.title} · LoopGroup` : "LoopGroup";
  }, [state]);

  return (
    <div className="noir card-page">
      <NoirBar pill={{ to: "/start", label: "See your own loops" }} />

      <main className="card-page__main">
        {state.status === "loading" && <p className="card-page__state" role="status">Reading the card…</p>}
        {state.status === "error" && <p className="card-page__state" role="alert">{state.message}</p>}
        {state.status === "missing" && (
          <section className="card-page__missing" aria-labelledby="card-missing-title">
            <p className="noir__eyebrow">Signal ledger</p>
            <h1 id="card-missing-title" className="noir__display">This card is no longer available.</h1>
            <p>The link was revoked or never existed. Whoever published it can issue a new one at any time.</p>
            <Link className="noir__cta" to="/start">See your own loops <ArrowRight size={18} aria-hidden="true" /></Link>
          </section>
        )}
        {state.status === "ready" && <Card card={state.card} token={token} pageUrl={pageUrl} />}
      </main>

      <NoirFooter />
    </div>
  );
}

function Card({ card, token, pageUrl }: { card: PublicCardPayload; token: string; pageUrl: string }) {
  const { totals } = card;
  const rate = totals.successRate7d;
  const rhythmSummary = `${totals.runs7d} runs over seven days: ${card.rhythm.reduce((sum, day) => sum + day.succeeded, 0)} succeeded, ${card.rhythm.reduce((sum, day) => sum + day.failed, 0)} failed, and ${card.rhythm.reduce((sum, day) => sum + day.unresolved, 0)} remain open.`;
  return (
    <>
    <article className="card-page__card" aria-labelledby="card-title">
      <header className="card-page__head">
        <p className="noir__eyebrow">Signal ledger · {kindLabel(card.kind)}</p>
        <h1 id="card-title" className="card-page__title">{card.title}</h1>
        <p className="card-page__owner">{card.owner} · generated {formatRelative(card.generatedAt)}</p>
      </header>

      <dl className="card-page__stats" aria-label="Card totals">
        <div><dt>Loops</dt><dd>{totals.loops}</dd><small>{kindLabel(card.kind).toLowerCase()}</small></div>
        <div><dt>Runs this week</dt><dd>{totals.runs7d}</dd><small>{totals.resolved7d} resolved</small></div>
        <div><dt>Reliability</dt><dd className={rate == null ? "is-withheld" : ""}>{rate == null ? "—" : `${Math.round(rate * 100)}%`}</dd><small>{rate == null ? "needs 3 resolved outcomes" : `${totals.resolved7d} resolved`}</small></div>
      </dl>

      <section className="card-page__rhythm" aria-labelledby="card-rhythm-title">
        <div className="card-page__section-head">
          <h2 id="card-rhythm-title" className="noir__eyebrow">Rhythm · 7 days</h2>
          <div className="card-page__legend" aria-label="Chart legend"><span><i className="is-success" />Succeeded</span><span><i className="is-failed" />Failed</span><span><i className="is-neutral" />Open</span></div>
        </div>
        <RhythmChart points={card.rhythm} summary={rhythmSummary} />
      </section>

      <section className="card-page__loops" aria-label="Loops on this card">
        <div className="card-page__cols" aria-hidden="true"><span>Loop</span><span>Cadence</span><span>Evidence</span><span>Reliability</span><span>Last signal</span></div>
        {card.loops.map((loop, index) => (
          <div className="card-page__row" key={`${loop.name}-${index}`}>
            <span className="noir__row-name">
              <ProviderMark provider={loop.provider} size={32} />
              <span className="card-page__identity"><strong>{loop.name}</strong>{loop.purpose && <small>{loop.purpose}</small>}</span>
            </span>
            <span className="card-page__cadence"><Clock3 size={12} aria-hidden="true" />{loop.cadence}</span>
            <span><EvidenceChip stage={loop.evidence}>{evidenceLabels[loop.evidence]}</EvidenceChip></span>
            <span className="noir__health">{loop.reliability ? <><i aria-hidden="true" />{Math.round(loop.reliability.rate * 100)}%<small> · {loop.reliability.resolved}</small></> : <span className="card-page__withheld" title="Reliability appears after 3 resolved outcomes">—</span>}</span>
            <span className="noir__signal">{loop.lastSignal ?? "No runs reported yet"}</span>
          </div>
        ))}
      </section>

      <footer className="card-page__actions">
        <CopyButton value={pageUrl} label="Copy link" />
        <a className="button button--secondary" href={`/og/${encodeURIComponent(token)}.png`} download={`${fileSlug(card.title)}.png`}><Download size={15} aria-hidden="true" /> Download PNG</a>
        <Link className="noir__cta noir__cta--small" to="/start">See your own loops <ArrowRight size={15} aria-hidden="true" /></Link>
      </footer>
    </article>
    <p className="noir__note">Shared from LoopGroup · reliability counts resolved outcomes only</p>
    </>
  );
}
